import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  CheckCircle, XCircle, Clock, AlertCircle, ChevronRight,
  Syringe, Scale, Calendar, Eye, Filter, CheckSquare, Square
} from 'lucide-react'
import { getPendingEvents, verifyEvent, rejectEvent, verifyEventsBulk } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { renderDate } from '../../utils/time'

// Event types whose approval triggers animal-state side effects (status
// change, calf creation, sale income). These are skipped by bulk approve —
// they must be reviewed one at a time — so we never offer them for selection.
const SIDE_EFFECT_TYPES = new Set(['death', 'sale', 'lost', 'castration', 'calving'])

export default function PendingEventsPage({ ranchId }) {
  const queryClient = useQueryClient()
  const { userId, ranchTimezone } = useAuth()
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [filterType, setFilterType] = useState('')
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [showBulkConfirm, setShowBulkConfirm] = useState(false)

  // Fetch pending events
  const { data: events, isLoading, error } = useQuery({
    queryKey: ['pendingEvents', ranchId, filterType],
    queryFn: () => getPendingEvents(ranchId, filterType || null),
  })

  const invalidatePending = () => {
    queryClient.invalidateQueries(['pendingEvents'])
    queryClient.invalidateQueries(['pendingEventsCount'])
    queryClient.invalidateQueries(['approvalsInboxCount'])
  }

  // Events eligible for bulk selection (side-effect-free only).
  const bulkEligible = (events || []).filter(e => !SIDE_EFFECT_TYPES.has(e.event_type))
  const allEligibleSelected = bulkEligible.length > 0 && bulkEligible.every(e => selectedIds.has(e.id))

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  const toggleSelectAll = () => {
    setSelectedIds(allEligibleSelected ? new Set() : new Set(bulkEligible.map(e => e.id)))
  }

  // Verify mutation
  const verifyMutation = useMutation({
    mutationFn: (eventId) => verifyEvent(eventId, userId),
    onSuccess: invalidatePending,
  })

  // Bulk verify: either the selected ids, or every matching pending event.
  const bulkVerifyMutation = useMutation({
    mutationFn: ({ eventIds, verifyAll }) =>
      verifyEventsBulk(ranchId, {
        eventIds: verifyAll ? null : eventIds,
        verifyAll,
        eventType: verifyAll ? (filterType || null) : null,
      }),
    onSuccess: (res) => {
      invalidatePending()
      setSelectedIds(new Set())
      setShowBulkConfirm(false)
      if (res?.skipped_count > 0) {
        alert(`${res.verified} approved. ${res.skipped_count} state-changing event(s) (deaths, sales, calvings) were skipped — review those individually.`)
      }
    },
  })

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ eventId, notes }) => rejectEvent(eventId, userId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries(['pendingEvents'])
      queryClient.invalidateQueries(['pendingEventsCount'])
      setShowRejectModal(false)
      setSelectedEvent(null)
      setRejectReason('')
    },
  })

  const handleVerify = (eventId) => {
    verifyMutation.mutate(eventId)
  }

  const handleRejectClick = (event) => {
    setSelectedEvent(event)
    setShowRejectModal(true)
  }

  const handleRejectConfirm = () => {
    if (selectedEvent && rejectReason.trim()) {
      rejectMutation.mutate({ eventId: selectedEvent.id, notes: rejectReason })
    }
  }

  const getEventIcon = (type) => {
    switch (type) {
      case 'vaccination':
        return <Syringe className="h-5 w-5 text-blue-600" />
      case 'treatment':
        return <Syringe className="h-5 w-5 text-purple-600" />
      case 'weight':
      case 'weighing':
        return <Scale className="h-5 w-5 text-green-600" />
      case 'calving':
        return <Calendar className="h-5 w-5 text-pink-600" />
      default:
        return <Eye className="h-5 w-5 text-gray-600" />
    }
  }

  const getEventTypeLabel = (type) => {
    const labels = {
      vaccination: 'Vaccination',
      treatment: 'Treatment',
      weight: 'Weight',
      weighing: 'Weight',
      calving: 'Calving',
      weaning: 'Weaning',
      injury: 'Injury',
      observation: 'Observation',
      movement: 'Movement',
      sale: 'Sale',
      death: 'Death',
    }
    return labels[type] || type
  }

  const formatEventData = (event) => {
    const data = event.event_data || {}
    switch (event.event_type) {
      case 'vaccination':
        return data.vaccine_name || 'Vaccine recorded'
      case 'treatment':
        return data.treatment_name || data.medication || 'Treatment recorded'
      case 'weight':
      case 'weighing':
        return data.weight_kg ? `${data.weight_kg} kg` : 'Weight recorded'
      case 'calving':
        return data.calf_sex ? `${data.calf_sex} calf` : 'Calving recorded'
      default:
        return data.notes || 'Event recorded'
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pending Approvals</h1>
          <p className="text-gray-600">
            Review and approve events entered by workers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">All Types</option>
            <option value="vaccination">Vaccinations</option>
            <option value="treatment">Treatments</option>
            <option value="weighing">Weights</option>
            <option value="calving">Calvings</option>
            <option value="observation">Observations</option>
          </select>
        </div>
      </div>

      {/* Bulk action toolbar */}
      {!isLoading && !error && events?.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 bg-white rounded-lg shadow">
          <button
            onClick={toggleSelectAll}
            disabled={bulkEligible.length === 0}
            className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-40"
          >
            {allEligibleSelected ? <CheckSquare className="h-5 w-5 text-green-600" /> : <Square className="h-5 w-5" />}
            Select all approvable ({bulkEligible.length})
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => bulkVerifyMutation.mutate({ eventIds: [...selectedIds], verifyAll: false })}
              disabled={selectedIds.size === 0 || bulkVerifyMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40"
            >
              <CheckCircle className="h-4 w-4" />
              Approve {selectedIds.size} selected
            </button>
            <button
              onClick={() => setShowBulkConfirm(true)}
              disabled={bulkVerifyMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-40"
            >
              Approve all pending{filterType ? ` ${filterType}s` : ''}
            </button>
          </div>
        </div>
      )}

      {/* Events List */}
      {isLoading ? (
        <div className="text-center py-12">Loading pending events...</div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">Error loading events</div>
      ) : events?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <CheckCircle className="h-12 w-12 mx-auto text-green-400 mb-4" />
          <p className="text-gray-500 text-lg">All caught up!</p>
          <p className="text-gray-400">No pending events to review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {events?.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                {/* Bulk-select checkbox (side-effect-free events only) */}
                {!SIDE_EFFECT_TYPES.has(event.event_type) && (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(event.id)}
                    onChange={() => toggleSelect(event.id)}
                    className="h-5 w-5 mt-3 rounded text-green-600 cursor-pointer"
                    aria-label="Select event for bulk approval"
                  />
                )}
                {/* Event Icon */}
                <div className="p-3 bg-gray-100 rounded-lg">
                  {getEventIcon(event.event_type)}
                </div>

                {/* Event Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">
                      {getEventTypeLabel(event.event_type)}
                    </span>
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Pending
                    </span>
                  </div>

                  <Link
                    to={`/cattle/${event.animal_id}`}
                    className="text-green-600 hover:text-green-700 font-medium"
                  >
                    {event.animal_name || event.animal_rfid}
                    <ChevronRight className="h-4 w-4 inline" />
                  </Link>

                  <p className="text-gray-600 mt-1">
                    {formatEventData(event)}
                  </p>

                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span>
                      {renderDate(event.event_date, ranchTimezone)}
                    </span>
                    {event.reporter_name && (
                      <span>
                        Reported by: {event.reporter_name}
                      </span>
                    )}
                    <span className="capitalize">
                      via {event.report_channel}
                    </span>
                  </div>

                  {/* Event Data Details */}
                  {event.event_data && Object.keys(event.event_data).length > 0 && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(event.event_data).map(([key, value]) => (
                          <div key={key}>
                            <span className="text-gray-500 capitalize">
                              {key.replace(/_/g, ' ')}:
                            </span>{' '}
                            <span className="text-gray-900">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleVerify(event.id)}
                    disabled={verifyMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleRejectClick(event)}
                    disabled={rejectMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bulk approve confirm */}
      {showBulkConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowBulkConfirm(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Approve all pending{filterType ? ` ${filterType}s` : ''}?</h3>
                <p className="text-sm text-gray-500">
                  This verifies every pending {filterType || 'cattle'} event for this ranch.
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              State-changing events (deaths, sales, calvings) are <strong>not</strong> included —
              review those individually so their animal records update correctly.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowBulkConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => bulkVerifyMutation.mutate({ verifyAll: true })}
                disabled={bulkVerifyMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                {bulkVerifyMutation.isPending ? 'Approving…' : 'Approve all'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowRejectModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Reject Event</h3>
                <p className="text-sm text-gray-500">
                  Please provide a reason for rejection
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for rejection *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                rows={3}
                placeholder="e.g., Incorrect dosage recorded, duplicate entry..."
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim() || rejectMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <XCircle className="h-4 w-4" />
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
